import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  ToggleButtonGroup,
  ToggleButton,
  Collapse,
  Link,
  CircularProgress,
  Chip,
  TextField,
} from "@mui/material";
import { CheckCircle, ErrorOutline, ExpandMore, ExpandLess, Bolt } from "@mui/icons-material";
import SavingOverlay from "../components/SavingOverlay";
import {
  aiIntegrationService,
  AiIntegrationOption,
  AiConnectionTestResult,
  modelLabel,
} from "../services/aiIntegrationService";
import { colors, typography } from "../theme/designSystem";

interface Props {
  entityId: string;
  onToast: (message: string, type: "success" | "error") => void;
}

const EFFORTS: { value: string; label: string }[] = [
  { value: "low", label: "Baixo" },
  { value: "medium", label: "Médio" },
  { value: "high", label: "Alto" },
];

// Configuração do provedor de IA da entidade (M1): fica abaixo do aceite do termo, na mesma tela.
// A chave é por tenant, na env — esta tela não coleta chave.
const AiIntegrationConfig: React.FC<Props> = ({ entityId, onToast }) => {
  const [options, setOptions] = useState<AiIntegrationOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<AiConnectionTestResult | null>(null);
  const [showHelp, setShowHelp] = useState(false);

  const [integrationId, setIntegrationId] = useState("");
  const [model, setModel] = useState("");
  const [effort, setEffort] = useState("medium");
  // Teto diário de respostas automáticas no WhatsApp (RN-618). Vazio = padrão do sistema.
  const [dailyCap, setDailyCap] = useState("");
  // Orçamento mensal de IA em US$ (harness H0, D-HAR-9). Vazio = sem orçamento.
  const [budget, setBudget] = useState("");
  const [alertPct, setAlertPct] = useState("80");
  const [onExceed, setOnExceed] = useState<"warn" | "block">("warn");
  const [monthSpent, setMonthSpent] = useState(0);
  // Snapshot do que está salvo — o botão Salvar só habilita quando o estado atual difere.
  const [saved, setSaved] = useState<
    { integrationId: string; model: string; effort: string; dailyCap: string; budget: string; alertPct: string; onExceed: string } | null>(null);

  const selected = useMemo(
    () => options.find((o) => o.id === integrationId),
    [options, integrationId]
  );

  const dirty = useMemo(
    () =>
      saved !== null &&
      (saved.integrationId !== integrationId ||
        saved.model !== model ||
        saved.effort !== effort ||
        saved.dailyCap !== dailyCap ||
        saved.budget !== budget ||
        saved.alertPct !== alertPct ||
        saved.onExceed !== onExceed),
    [saved, integrationId, model, effort, dailyCap, budget, alertPct, onExceed]
  );

  const budgetValue = Number(budget.replace(",", ".")) || 0;
  const spentPct = budgetValue > 0 ? Math.round((monthSpent / budgetValue) * 100) : 0;
  const usd = (v: number) => v.toLocaleString("pt-BR", { style: "currency", currency: "USD", minimumFractionDigits: 2, maximumFractionDigits: 2 });

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [opts, selection] = await Promise.all([
          aiIntegrationService.getOptions(entityId),
          aiIntegrationService.getSelection(entityId),
        ]);
        setOptions(opts);

        const initialId = selection.integrationId || opts[0]?.id || "";
        const initial = opts.find((o) => o.id === initialId);
        const m = selection.model || initial?.defaultModel || "";
        const e = selection.reasoningEffort || "medium";
        const cap = selection.maxRepliesPerDay != null ? String(selection.maxRepliesPerDay) : "";
        const b = selection.monthlyBudgetUsd != null ? String(selection.monthlyBudgetUsd).replace(".", ",") : "";
        const a = String(selection.budgetAlertPercent ?? 80);
        const x = selection.budgetBlocks ? "block" : "warn";
        setIntegrationId(initialId);
        setModel(m);
        setEffort(e);
        setDailyCap(cap);
        setBudget(b);
        setAlertPct(a);
        setOnExceed(x);
        setMonthSpent(selection.monthSpentUsd ?? 0);
        // Só marca como "salvo" (base do dirty) o que já estava configurado; escolha herdada/inexistente
        // deixa o botão habilitado para o admin confirmar a primeira gravação.
        setSaved(selection.configured && !selection.inherited
          ? { integrationId: initialId, model: m, effort: e, dailyCap: cap, budget: b, alertPct: a, onExceed: x }
          : null);
      } catch (error) {
        console.error("Erro ao carregar a configuração de IA:", error);
        onToast("Erro ao carregar a configuração de IA.", "error");
      } finally {
        setLoading(false);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const changeProvider = (id: string) => {
    setIntegrationId(id);
    setTestResult(null);
    const provider = options.find((o) => o.id === id);
    // Ao trocar de provedor, o modelo escolhido pode não existir no novo — volta ao default.
    if (provider && !provider.models.includes(model)) {
      setModel(provider.defaultModel);
    }
  };

  const handleTest = async () => {
    if (!integrationId || testing) return;
    setTesting(true);
    setTestResult(null);
    try {
      const result = await aiIntegrationService.testConnection(entityId, integrationId, model || null);
      setTestResult(result);
    } catch (error: unknown) {
      const message =
        (error as { response?: { data?: { description?: string } } })?.response?.data?.description ||
        "Não foi possível testar a conexão.";
      setTestResult({ success: false, model, latencyMs: 0, error: message });
    } finally {
      setTesting(false);
    }
  };

  const handleSave = async () => {
    if (!integrationId || saving) return;
    setSaving(true);
    try {
      await aiIntegrationService.save({
        entityId,
        integrationId,
        model: model || null,
        reasoningEffort: effort,
        enabled: true,
        maxRepliesPerDay: Number(dailyCap) || null,
        monthlyBudgetUsd: budgetValue > 0 ? budgetValue : null,
        budgetAlertPercent: Number(alertPct) || null,
        budgetBlocks: budgetValue > 0 && onExceed === "block",
      });
      setSaved({ integrationId, model, effort, dailyCap, budget, alertPct, onExceed }); // agora limpo — desabilita o Salvar
      onToast("Configuração de IA salva com sucesso.", "success");
    } catch (error: unknown) {
      const message =
        (error as { response?: { data?: { description?: string } } })?.response?.data?.description ||
        "Erro ao salvar a configuração de IA.";
      onToast(message, "error");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress size={24} sx={{ color: colors.primary }} />
      </Box>
    );
  }

  if (options.length === 0) {
    return (
      <Typography variant="body2" sx={{ color: colors.textSecondary, mt: 2 }}>
        Nenhum provedor de IA disponível no momento.
      </Typography>
    );
  }

  return (
    <Box sx={{ position: "relative", mt: 3, pt: 3, borderTop: "1px solid #e9ecef" }}>
      <SavingOverlay open={saving} message="Salvando configuração..." />

      <Typography
        variant="h6"
        sx={{ fontSize: "1.05rem", fontWeight: 600, color: colors.textPrimary, mb: 2 }}
      >
        Configuração da IA
      </Typography>

      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mb: 2 }}>
        <FormControl size="small" sx={{ minWidth: 240, flex: "1 1 240px" }}>
          <InputLabel>Provedor de IA</InputLabel>
          <Select
            label="Provedor de IA"
            value={integrationId}
            onChange={(e) => changeProvider(e.target.value)}
          >
            {options.map((o) => (
              <MenuItem key={o.id} value={o.id}>
                {o.displayName}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 260, flex: "1 1 260px" }}>
          <InputLabel>Modelo</InputLabel>
          <Select label="Modelo" value={model} onChange={(e) => setModel(e.target.value)}>
            {(selected?.models || []).map((m) => (
              <MenuItem key={m} value={m}>
                {modelLabel(m)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 4, mb: 2 }}>
        <Box sx={{ flex: "1 1 240px", minWidth: 220 }}>
          <Typography variant="body2" sx={{ color: colors.textPrimary, mb: 0.5 }}>
            Esforço de raciocínio
          </Typography>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={effort}
            onChange={(_, v) => v && setEffort(v)}
          >
            {EFFORTS.map((e) => (
              <ToggleButton key={e.value} value={e.value} sx={{ textTransform: "none" }}>
                {e.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
          <Typography variant="caption" sx={{ display: "block", color: colors.textSecondary, mt: 0.5 }}>
            Quanto o modelo "pensa" antes de responder. Alto custa mais tempo e tokens.
          </Typography>
        </Box>
        <Box sx={{ flex: "1 1 240px", minWidth: 220 }}>
          <Typography variant="body2" sx={{ color: colors.textPrimary, mb: 0.5 }}>
            Limite diário de respostas automáticas
          </Typography>
          <TextField
            value={dailyCap}
            onChange={(event) => setDailyCap(event.target.value.replace(/\D/g, "").slice(0, 5))}
            size="small"
            placeholder="300"
            inputProps={{ maxLength: 5, inputMode: "numeric" }}
            sx={{ maxWidth: 160 }}
          />
          <Typography variant="caption" sx={{ display: "block", color: colors.textSecondary, mt: 0.5 }}>
            Quantas respostas os assistentes podem enviar por dia no WhatsApp. Atingido o limite, os
            leads entram como pendência no painel para atendimento humano. Em branco usa o padrão do
            sistema (300).
          </Typography>
        </Box>
      </Box>

      {/* Orçamento mensal de IA (harness H0, D-HAR-9): em US$, a moeda do provedor */}
      <Box sx={{ mb: 2, p: "1rem", border: `1px solid ${colors.backgroundAlt}`, borderRadius: "8px" }}>
        <Typography variant="body2" sx={{ color: colors.textPrimary, fontWeight: 600, mb: 0.25 }}>
          Orçamento mensal de IA
        </Typography>
        <Typography variant="caption" sx={{ display: "block", color: colors.textSecondary, mb: 1.5 }}>
          Gasto neste mês: <strong style={{ color: colors.textPrimary }}>{usd(monthSpent)}</strong>
          {budgetValue > 0 && <> de {usd(budgetValue)} ({spentPct}%)</>}. Valores em dólar, a moeda do
          provedor de IA; o mês fecha no dia 1º. O Suporte Clinic4Us não conta: é custo da plataforma.
        </Typography>
        {budgetValue > 0 && spentPct >= (Number(alertPct) || 80) && (
          <Chip
            size="small"
            label={spentPct >= 100 ? "Orçamento atingido" : `Acima de ${Number(alertPct) || 80}% do orçamento`}
            sx={{ mb: 1.5, backgroundColor: spentPct >= 100 ? colors.errorSoft : "#fff3cd", color: spentPct >= 100 ? colors.errorHover : "#856404", fontWeight: 600 }}
          />
        )}
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, alignItems: "flex-start" }}>
          <TextField
            label="Orçamento (US$ por mês)"
            value={budget}
            onChange={(event) => setBudget(event.target.value.replace(/[^\d,]/g, "").slice(0, 9))}
            size="small"
            placeholder="Sem orçamento"
            inputProps={{ inputMode: "decimal", maxLength: 9 }}
            sx={{ flex: "1 1 180px", maxWidth: 220 }}
          />
          <TextField
            label="Avisar a partir de (%)"
            value={alertPct}
            onChange={(event) => setAlertPct(event.target.value.replace(/\D/g, "").slice(0, 3))}
            size="small"
            disabled={budgetValue <= 0}
            inputProps={{ inputMode: "numeric", maxLength: 3 }}
            sx={{ flex: "1 1 160px", maxWidth: 200 }}
          />
          <Box sx={{ flex: "1 1 240px" }}>
            <ToggleButtonGroup
              exclusive
              size="small"
              value={onExceed}
              disabled={budgetValue <= 0}
              onChange={(_, v) => v && setOnExceed(v)}
              aria-label="Ao atingir o orçamento"
            >
              <ToggleButton value="warn" sx={{ textTransform: "none" }}>Só avisar</ToggleButton>
              <ToggleButton value="block" sx={{ textTransform: "none" }}>Bloquear</ToggleButton>
            </ToggleButtonGroup>
            <Typography variant="caption" sx={{ display: "block", color: colors.textSecondary, mt: 0.5 }}>
              Bloquear: atingido o orçamento, os assistentes de IA da clínica param até o mês virar ou o
              orçamento subir. Em branco, sem orçamento.
            </Typography>
          </Box>
        </Box>
      </Box>

      {(selected?.setupInstructions || selected?.docsLink) && (
        <Box sx={{ mb: 2 }}>
          <Button
            onClick={() => setShowHelp((s) => !s)}
            startIcon={showHelp ? <ExpandLess /> : <ExpandMore />}
            sx={{ textTransform: "none", color: colors.primary, px: 0 }}
          >
            Como configurar
          </Button>
          <Collapse in={showHelp}>
            <Box
              sx={{
                fontSize: typography.fontSize.sm,
                color: colors.textPrimary,
                backgroundColor: colors.background,
                border: "1px solid #e9ecef",
                borderRadius: "8px",
                p: "1rem",
                whiteSpace: "pre-wrap",
              }}
            >
              {selected?.setupInstructions}
              {selected?.docsLink && (
                <Box sx={{ mt: 1 }}>
                  <Link href={selected.docsLink} target="_blank" rel="noopener" sx={{ color: colors.primary }}>
                    Documentação do provedor
                  </Link>
                </Box>
              )}
              {selected && !selected.supportsSensitiveData && (
                <Box sx={{ mt: 1 }}>
                  <Chip
                    size="small"
                    label="Não liberado para dado clínico sensível"
                    sx={{ backgroundColor: "#fff3cd", color: "#856404" }}
                  />
                </Box>
              )}
            </Box>
          </Collapse>
        </Box>
      )}

      <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
        <Button
          onClick={handleSave}
          disabled={saving || !integrationId || (saved !== null && !dirty)}
          variant="contained"
          sx={{ textTransform: "none", backgroundColor: colors.primary }}
        >
          Salvar configuração
        </Button>
        <Button
          onClick={handleTest}
          disabled={testing || !integrationId}
          variant="outlined"
          startIcon={testing ? <CircularProgress size={16} /> : <Bolt />}
          sx={{ textTransform: "none", borderColor: colors.primary, color: colors.primary }}
        >
          Testar conexão
        </Button>

        {testResult && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            {testResult.success ? (
              <>
                <CheckCircle sx={{ color: colors.success, fontSize: 20 }} />
                <Typography variant="body2" sx={{ color: colors.textPrimary }}>
                  Conexão OK ({testResult.model}, {testResult.latencyMs} ms)
                </Typography>
              </>
            ) : (
              <>
                <ErrorOutline sx={{ color: colors.error, fontSize: 20 }} />
                <Typography variant="body2" sx={{ color: colors.error }}>
                  {testResult.error || "Falha na conexão."}
                </Typography>
              </>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default AiIntegrationConfig;
